import React, { useState, useEffect } from 'react';
import { X, Check, Plus, AlertCircle } from 'lucide-react';
import { useAuth } from '../../../App';
import { useTranslation } from '../../../contexts/LanguageContext';

const AddSubscriptionForm = ({ 
  userSubscriptions = [],
  currentSubscriptions = [],
  onAdd, 
  onCancel,
  isProcessing
}) => {
  const { t } = useTranslation();
  const { httpClient } = useAuth(); // Get httpClient from auth context
  
  const [selectedSubscriptions, setSelectedSubscriptions] = useState([]);
  const [error, setError] = useState('');
  const [conflictedSubscriptions, setConflictedSubscriptions] = useState([]);
  const [currentSpaceMerchants, setCurrentSpaceMerchants] = useState([]);
  
  useEffect(() => {
    const fetchConflicts = async () => {
      try {
        if (!httpClient) return;
        
        const response = await httpClient.request('/api/user/subscriptions-in-other-spaces');
        
        if (response.ok) {
          const merchantIds = await response.json();
          setConflictedSubscriptions(merchantIds);
        }
      } catch (error) {
        console.error('Error fetching subscription conflicts:', error);
      }
    };
    
    fetchConflicts();
  }, [httpClient]);

  useEffect(() => {
    const merchantIds = currentSubscriptions.map(plan => plan.merchant_id);
    setCurrentSpaceMerchants(merchantIds);
  }, [currentSubscriptions]);

  const availableSubs = userSubscriptions.filter(sub => 
    !currentSpaceMerchants.includes(sub.merchant_id) &&
    !conflictedSubscriptions.includes(sub.merchant_id)
  );
  
  const currentSpaceSubs = userSubscriptions.filter(sub => 
    currentSpaceMerchants.includes(sub.merchant_id)
  );
  
  const otherSpaceSubs = userSubscriptions.filter(sub => 
    conflictedSubscriptions.includes(sub.merchant_id) &&
    !currentSpaceMerchants.includes(sub.merchant_id)
  );

  const handleSubscriptionToggle = (subscriptionId) => {
    setSelectedSubscriptions(prev => 
      prev.includes(subscriptionId)
        ? prev.filter(id => id !== subscriptionId)
        : [...prev, subscriptionId]
    );
    
    if (error) setError('');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (selectedSubscriptions.length === 0) {
      setError(t('sharesubs.addSubscriptionForm.selectAtLeastOne'));
      return;
    }
    
    onAdd(selectedSubscriptions);
  };

  return (
    <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-md font-semibold text-gray-800">
          {t('sharesubs.addSubscriptionForm.title')}
        </h3>
        <button 
          onClick={onCancel} 
          className="text-gray-500 hover:text-gray-700"
          disabled={isProcessing}
        >
          <X className="h-5 w-5" />
        </button>
      </div>
      
      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('sharesubs.addSubscriptionForm.selectSubscriptions')}
          </label>
          
          {availableSubs.length === 0 && 
           otherSpaceSubs.length === 0 && 
           currentSpaceSubs.length === 0 ? (
            <div className="text-center py-4">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gray-100 flex items-center justify-center">
                <Plus className="h-6 w-6 text-gray-400" />
              </div>
              <p className="text-gray-500">
                {t('sharesubs.addSubscriptionForm.noSubscriptionsAvailable')}
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-2 max-h-60 overflow-y-auto mb-2">
                {availableSubs.map(subscription => (
                  <div 
                    key={subscription.id}
                    onClick={() => handleSubscriptionToggle(subscription.id)}
                    className={`p-2 border rounded-lg cursor-pointer transition-colors ${
                      selectedSubscriptions.includes(subscription.id)
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        {subscription.logo ? (
                          <img 
                            src={subscription.logo} 
                            alt={subscription.name}
                            className="w-8 h-8 rounded mr-2 object-contain"
                          />
                        ) : (
                          <div className="w-8 h-8 rounded mr-2 bg-gray-200 flex items-center justify-center">
                            <div className="h-4 w-4 text-gray-400" />
                          </div>
                        )}
                        <div>
                          <div className="font-medium text-sm">{subscription.name}</div>
                          <div className="text-xs text-gray-500">
                            €{subscription.price} / {subscription.billing_interval}
                          </div>
                        </div>
                      </div>
                      {selectedSubscriptions.includes(subscription.id) && (
                        <Check className="h-4 w-4 text-blue-500" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
              
              {currentSpaceSubs.length > 0 && (
                <div className="mt-4">
                  <div className="flex items-center text-sm font-medium text-gray-600 mb-2">
                    <Check className="h-4 w-4 mr-1 text-green-500" />
                    {t('sharesubs.addSubscriptionForm.alreadyInThisSpace')}
                  </div>
                  <div className="border border-gray-200 rounded-lg bg-gray-50 p-2">
                    {currentSpaceSubs.map(subscription => (
                      <div 
                        key={subscription.id}
                        className="p-2 border-b border-gray-100 last:border-b-0 flex items-center opacity-70"
                      >
                        <div className="flex items-center flex-1">
                          {subscription.logo ? (
                            <img 
                              src={subscription.logo} 
                              alt={subscription.name}
                              className="w-6 h-6 rounded mr-2 object-contain"
                            />
                          ) : (
                            <div className="w-6 h-6 rounded mr-2 bg-gray-200 flex items-center justify-center">
                              <div className="h-3 w-3 text-gray-400" />
                            </div>
                          )}
                          <div>
                            <div className="font-medium text-sm">{subscription.name}</div>
                          </div>
                        </div>
                        <div className="text-xs text-gray-600 italic">
                          {t('sharesubs.addSubscriptionForm.alreadyAdded')}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {otherSpaceSubs.length > 0 && (
                <div className="mt-4">
                  <div className="flex items-center text-sm font-medium text-yellow-700 mb-2">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {t('sharesubs.addSubscriptionForm.alreadyInOtherSpaces')}
                  </div>
                  <div className="border border-yellow-200 rounded-lg bg-yellow-50 p-2">
                    {otherSpaceSubs.map(subscription => (
                      <div 
                        key={subscription.id}
                        className="p-2 border-b border-yellow-100 last:border-b-0 flex items-center opacity-60"
                      >
                        <div className="flex items-center flex-1">
                          {subscription.logo ? (
                            <img 
                              src={subscription.logo} 
                              alt={subscription.name}
                              className="w-6 h-6 rounded mr-2 object-contain"
                            />
                          ) : (
                            <div className="w-6 h-6 rounded mr-2 bg-gray-200 flex items-center justify-center">
                              <div className="h-3 w-3 text-gray-400" />
                            </div>
                          )}
                          <div>
                            <div className="font-medium text-sm">{subscription.name}</div>
                          </div>
                        </div>
                        <div className="text-xs text-yellow-700 italic">
                          {t('sharesubs.addSubscriptionForm.inAnotherSpace')}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {error && (
                <div className="text-xs text-red-500 mt-1 text-center">
                  {error}
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex justify-end space-x-2 mt-4">
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100"
            disabled={isProcessing}
          >
            {t('sharesubs.actions.cancel')}
          </button>
          <button
            type="submit"
            className={`px-3 py-1.5 text-sm rounded-lg text-white ${
              selectedSubscriptions.length === 0 || isProcessing
                ? 'bg-gray-300 cursor-not-allowed'
                : 'bg-[#253985] hover:bg-[#1a2c6d]'
            }`}
            disabled={selectedSubscriptions.length === 0 || isProcessing}
          >
            {isProcessing ? (
              <span className="flex items-center">
                <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                {t('sharesubs.addSubscriptionForm.adding')}
              </span>
            ) : (
              `${t('sharesubs.addSubscriptionForm.addToSpace')} (${selectedSubscriptions.length})`
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddSubscriptionForm;